import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser'
import { HEALTH_REDIRECT_URI } from '../constants'
import { scopes } from 'api/authConfig'

export type AuthSessionMode = 'local' | 'azure'

const AUTH_SESSION_MODE_KEY = 'caleo_auth_session_mode'
const AZURE_ACCESS_TOKEN_KEY = 'caleo_azure_access_token'

let azureMsalInstance: IPublicClientApplication | null = null
let azureBootstrapPromise: Promise<void> | null = null
let azureBootstrapResolve: (() => void) | null = null
let azureBootstrapReject: ((reason: unknown) => void) | null = null
let azureAccount: AccountInfo | null = null

export function setAzureMsalInstance(instance: IPublicClientApplication | null): void {
  azureMsalInstance = instance
}

export function getAzureMsalInstance(): IPublicClientApplication | null {
  return azureMsalInstance
}

export function setAzureAccount(account: AccountInfo | null): void {
  azureAccount = account
}

export function getAzureAccount(): AccountInfo | null {
  return azureAccount
}

export function setAuthSessionMode(mode: AuthSessionMode | null): void {
  if (mode === null) {
    sessionStorage.removeItem(AUTH_SESSION_MODE_KEY)
    return
  }

  sessionStorage.setItem(AUTH_SESSION_MODE_KEY, mode)
}

export function getAuthSessionMode(): AuthSessionMode | null {
  const mode = sessionStorage.getItem(AUTH_SESSION_MODE_KEY)

  if (mode === 'local' || mode === 'azure') {
    return mode
  }

  return null
}

export function setAzureAccessToken(token: string | null): void {
  if (token === null) {
    sessionStorage.removeItem(AZURE_ACCESS_TOKEN_KEY)
    return
  }

  sessionStorage.setItem(AZURE_ACCESS_TOKEN_KEY, token)
}

export function getAzureAccessToken(): string | null {
  return sessionStorage.getItem(AZURE_ACCESS_TOKEN_KEY)
}

export function clearAuthSession(): void {
  setAuthSessionMode(null)
  setAzureAccessToken(null)
}

export function beginAzureBootstrap(): Promise<void> {
  if (!azureBootstrapPromise) {
    azureBootstrapPromise = new Promise((resolve, reject) => {
      azureBootstrapResolve = () => {
        azureBootstrapPromise = null
        azureBootstrapResolve = null
        azureBootstrapReject = null
        resolve()
      }

      azureBootstrapReject = reason => {
        azureBootstrapPromise = null
        azureBootstrapResolve = null
        azureBootstrapReject = null
        reject(reason)
      }
    })
  }

  return azureBootstrapPromise
}

export function completeAzureBootstrap(): void {
  azureBootstrapResolve?.()
}

export function failAzureBootstrap(reason: unknown): void {
  azureBootstrapReject?.(reason)
}

let azureAccessTokenInFlight: Promise<string> | null = null

async function requestSilentAzureAccessToken(): Promise<string> {
  const instance = getAzureMsalInstance()

  if (!instance) {
    throw new Error('Azure MSAL instance is not available')
  }

  let account = instance.getActiveAccount()
  if (!account) {
    account = getAzureAccount()
    if (!account) {
      throw new Error('Azure account is not available')
    }
  }

  const result = await instance.acquireTokenSilent({
    account,
    scopes,
    redirectUri: HEALTH_REDIRECT_URI,
    forceRefresh: true,
  })

  setAuthSessionMode('azure')
  setAzureAccessToken(result.accessToken)

  return result.accessToken
}

/**
 * Acquires a fresh Azure access token, deduplicating concurrent callers onto a
 * single in-flight MSAL request so they all observe the same outcome instead of
 * racing independent silent-renewal calls against each other.
 */
export async function acquireSilentAzureAccessToken(): Promise<string> {
  if (!azureAccessTokenInFlight) {
    azureAccessTokenInFlight = requestSilentAzureAccessToken().finally(() => {
      azureAccessTokenInFlight = null
    })
  }

  return azureAccessTokenInFlight
}
